import axios from "axios";
import { debug } from "../../utils/debugLogger";

// LinkedIn OAuth Configuration
//
// Two LinkedIn apps are required because the Community Management API must be the ONLY
// product on its app. The "personal" app (DaisyAi) holds Sign In + Share on LinkedIn;
// the "business" app (Sagelyn) holds Community Management API only.
const LINKEDIN_CLIENT_ID = process.env.REACT_APP_LINKEDIN_CLIENT_ID || "";
// Note: Client secret is only used on backend for security - never expose in frontend
const LINKEDIN_REDIRECT_URI = process.env.REACT_APP_LINKEDIN_REDIRECT_URI || "";
const LINKEDIN_AUTH_BASE_URL = process.env.REACT_APP_LINKEDIN_AUTH_URL || "https://www.linkedin.com/oauth/v2/authorization";
// Note: Token exchange is handled by backend - no direct frontend access to token endpoint

// Business (Community Management) app — separate client ID + redirect.
const LINKEDIN_BUSINESS_CLIENT_ID = process.env.REACT_APP_LINKEDIN_BUSINESS_CLIENT_ID || "";
const LINKEDIN_BUSINESS_REDIRECT_URI = process.env.REACT_APP_LINKEDIN_BUSINESS_REDIRECT_URI || "";

// Personal app scopes — Sign In with OpenID Connect + Share on LinkedIn.
// r_member_postAnalytics requires Community Management product (business app only),
// so personal tokens will only return engagement via socialActions (likes/comments).
const LINKEDIN_PERSONAL_SCOPE = [
  "openid",
  "profile",
  "email",
  "w_member_social",        // Personal profile posting
].join(" ");

// Business app scopes — Community Management API (posting + analytics reads).
// Scope names must match exactly what the Sagelyn app offers on its Auth tab.
const LINKEDIN_BUSINESS_SCOPE = [
  "rw_organization_admin",  // List Pages the user administers + page/follower reporting
  "w_organization_social",  // Post to organization Pages
  "r_organization_social",  // Read org posts/engagement (analytics)
].join(" ");

// Backend response format - tokens are saved securely on backend, not returned
export interface LinkedInTokenResponse {
  success: boolean;
  scope: string;
  profile_id: string; // LinkedIn member ID (e.g., "urn:li:person:ABC123")
  profile: LinkedInProfile;
  organizations?: Array<{
    organization: string; // Organization URN (e.g., "urn:li:organization:XYZ789")
    role: string; // User's role (e.g., "ADMINISTRATOR")
  }>;
  message: string;
  // Note: access_token and refresh_token are NOT returned for security
}

export interface LinkedInProfile {
  sub: string; // LinkedIn member ID
  name: string;
  given_name: string;
  family_name: string;
  picture?: string;
  email: string;
  email_verified: boolean;
}

export interface LinkedInOrganization {
  id: string;
  name: string;
  vanityName: string;
  localizedName?: string;
  logoUrl?: string;
}

/**
 * Generate LinkedIn OAuth authorization URL
 * Includes both personal and org scopes (MDP approved).
 * @param state - Optional state parameter for security
 * @returns LinkedIn OAuth URL
 */
export function getLinkedInAuthUrl(state?: string): string {
  debug.log("LinkedIn Environment Variables:", {
    clientId: LINKEDIN_CLIENT_ID,
    redirectUri: LINKEDIN_REDIRECT_URI,
    envClientId: process.env.REACT_APP_LINKEDIN_CLIENT_ID,
  });

  if (!LINKEDIN_CLIENT_ID) {
    throw new Error("LinkedIn Client ID is not configured");
  }

  const params = new URLSearchParams({
    response_type: "code",
    client_id: LINKEDIN_CLIENT_ID,
    redirect_uri: LINKEDIN_REDIRECT_URI,
    scope: LINKEDIN_PERSONAL_SCOPE,
    prompt: "consent", // Request consent to ensure refresh token is returned
    ...(state ? { state } : {}),
  });

  return `${LINKEDIN_AUTH_BASE_URL}?${params.toString()}`;
}

/**
 * Generate the LinkedIn OAuth authorization URL for the *business* app (Community
 * Management API). Requests org scopes only; uses the dedicated business client ID
 * and redirect URI.
 * @param state - Optional state parameter for security
 * @returns LinkedIn OAuth URL for business Page connection
 */
export function getLinkedInBusinessAuthUrl(state?: string): string {
  if (!LINKEDIN_BUSINESS_CLIENT_ID) {
    throw new Error("LinkedIn business Client ID is not configured");
  }

  const params = new URLSearchParams({
    response_type: "code",
    client_id: LINKEDIN_BUSINESS_CLIENT_ID,
    redirect_uri: LINKEDIN_BUSINESS_REDIRECT_URI,
    scope: LINKEDIN_BUSINESS_SCOPE,
    prompt: "consent",
    ...(state ? { state } : {}),
  });

  return `${LINKEDIN_AUTH_BASE_URL}?${params.toString()}`;
}

/**
 * Exchange authorization code for access token via backend API
 * Backend saves tokens securely and returns profile data
 * @param code - Authorization code from LinkedIn callback
 * @param customerId - Customer ID for the backend endpoint (must be in URL path)
 * @param idToken - User's authentication token for backend API
 * @param redirectUri - The exact redirect URI used in OAuth URL (optional, falls back to env var)
 * @returns Token response with profile and organizations (no access_token - backend saves it)
 */
export async function exchangeLinkedInCodeForToken(
  code: string,
  customerId: string,
  idToken: string,
  redirectUri?: string
): Promise<LinkedInTokenResponse> {
  // Use social API URL or fallback to hardcoded URL
  const BACKEND_API_BASE = process.env.REACT_APP_SOCIAL_API_URL || 'https://sgoxw8yx7k.execute-api.us-east-1.amazonaws.com/Prod';
  
  if (!customerId) {
    throw new Error("Customer ID is required for LinkedIn token exchange");
  }
  
  if (!idToken) {
    throw new Error("User authentication token is required");
  }
  
  // Use provided redirect_uri or fallback to env variable
  const redirect_uri = redirectUri || LINKEDIN_REDIRECT_URI;
  
  try {
    // Call backend endpoint to exchange code for token
    // Backend will: 1) Exchange code for token, 2) Save token securely, 3) Return profile data
    // Request body format: { code, redirect_uri }
    const response = await axios.post(
      `${BACKEND_API_BASE}/${customerId}/linkedin/exchange-token`,
      {
        code,
        redirect_uri, // Must exactly match the redirect URI used in OAuth URL
      },
      {
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`,
        },
      }
    );
    
    // Backend returns: { success, scope, profile_id, profile, organizations, message }
    // Note: access_token is NOT returned for security reasons
    return response.data;
  } catch (backendError: any) {
    debug.error("LinkedIn token exchange error:", {
      status: backendError.response?.status,
      statusText: backendError.response?.statusText,
      data: backendError.response?.data,
      message: backendError.message,
      url: `${BACKEND_API_BASE}/${customerId}/linkedin/exchange-token`,
      requestBody: { code, redirect_uri },
    });
    
    // Match backend error codes
    if (backendError.response?.status === 400) {
      throw new Error(
        backendError.response?.data?.error || 
        backendError.response?.data?.message ||
        "Missing code or customer_id"
      );
    }
    
    if (backendError.response?.status === 401) {
      throw new Error(
        backendError.response?.data?.error || 
        backendError.response?.data?.message ||
        "Missing or invalid Cognito token"
      );
    }
    
    if (backendError.response?.status === 404) {
      throw new Error(
        "LinkedIn token exchange endpoint not found. Please verify the backend endpoint is deployed."
      );
    }
    
    if (backendError.response?.status === 500) {
      throw new Error(
        backendError.response?.data?.error || 
        backendError.response?.data?.message ||
        "LinkedIn client configuration incomplete"
      );
    }
    
    if (backendError.response?.status === 502) {
      throw new Error(
        backendError.response?.data?.error || 
        backendError.response?.data?.message ||
        "LinkedIn did not return access token"
      );
    }
    
    throw new Error(
      backendError.response?.data?.error || 
      backendError.response?.data?.message ||
      backendError.message || 
      "Failed to exchange LinkedIn authorization code"
    );
  }
}

/**
 * Get user profile information from LinkedIn
 * @param accessToken - LinkedIn access token
 * @returns User profile data
 */
export async function getLinkedInProfile(
  accessToken: string
): Promise<LinkedInProfile> {
  const response = await axios.get("https://api.linkedin.com/v2/userinfo", {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  return response.data;
}

/**
 * Get organizations that the user can manage
 * @param accessToken - LinkedIn access token
 * @returns List of organizations
 */
export async function getLinkedInOrganizations(
  accessToken: string
): Promise<LinkedInOrganization[]> {
  try {
    // Get organization access control info
    const response = await axios.get(
      "https://api.linkedin.com/v2/organizationAcls",
      {
        params: {
          q: "roleAssignee",
          role: "ADMINISTRATOR",
          projection: "(elements*(organization~(id,localizedName,vanityName,logoV2(original~:playableStreams))))",
        },
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "LinkedIn-Version": "202401",
        },
      }
    );

    // Extract organizations from response
    const organizations = response.data.elements?.map((element: any) => {
      const org = element["organization~"];

      // Extract logo URL if available
      let logoUrl: string | undefined;
      const logo = org.logoV2?.["original~"];
      if (logo?.elements && logo.elements.length > 0) {
        logoUrl = logo.elements[0]?.identifiers?.[0]?.identifier;
      }

      return {
        id: org.id,
        name: org.localizedName || org.vanityName,
        vanityName: org.vanityName,
        localizedName: org.localizedName,
        logoUrl,
      };
    }) || [];

    return organizations;
  } catch (error: any) {
    debug.error("Error fetching LinkedIn organizations:", error.response?.data || error.message);
    // Return empty array if user has no organizations
    return [];
  }
}

/**
 * Handle complete LinkedIn OAuth callback flow
 * Backend saves tokens securely and returns profile data
 * @param code - Authorization code from callback
 * @param customerId - Customer ID for token exchange
 * @param idToken - User's authentication token
 * @param redirectUri - The exact redirect URI used in OAuth URL (optional)
 * @returns Object with profile and organizations (no access_token - backend saves it)
 */
export async function handleLinkedInCallback(
  code: string,
  customerId: string,
  idToken: string,
  redirectUri?: string
) {
  try {
    // Exchange code via backend - backend saves token and returns profile
    const tokenResponse = await exchangeLinkedInCodeForToken(code, customerId, idToken, redirectUri);
    
    debug.log("LinkedIn token exchange successful:", {
      profileId: tokenResponse.profile_id,
      profileName: tokenResponse.profile?.name,
      organizationsCount: tokenResponse.organizations?.length || 0,
    });

    // Backend returns profile and organizations - no access_token (saved securely on backend)
    return {
      profile_id: tokenResponse.profile_id,
      profile: tokenResponse.profile,
      organizations: tokenResponse.organizations || [],
      scope: tokenResponse.scope,
      message: tokenResponse.message,
      // Note: access_token is NOT returned - backend saves it securely
    };
  } catch (error: any) {
    debug.error("LinkedIn authentication error:", error.response?.data || error.message);
    throw new Error(
      error.response?.data?.error_description ||
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      "Failed to authenticate with LinkedIn"
    );
  }
}

/**
 * Test posting to LinkedIn Personal Profile
 * Uses backend endpoint to avoid CORS issues
 * @param accessToken - LinkedIn access token
 * @param personUrn - Person URN (format: urn:li:person:ABC123)
 * @param message - Text to post
 * @param customerId - Customer ID for backend endpoint
 * @param idToken - User's authentication token
 */
export async function testLinkedInPersonalPost({
  accessToken,
  personUrn,
  message,
  customerId,
  idToken,
}: {
  accessToken: string;
  personUrn: string;
  message: string;
  customerId: string;
  idToken: string;
}) {
  try {
    // Call backend endpoint to post to LinkedIn (server-to-server, no CORS)
    const BACKEND_API_BASE = process.env.REACT_APP_SOCIAL_API_URL || 'https://bc631mqtg1.execute-api.us-east-1.amazonaws.com/Prod';
    
    const postData = {
      author: personUrn,
      lifecycleState: "PUBLISHED",
      specificContent: {
        "com.linkedin.ugc.ShareContent": {
          shareCommentary: {
            text: message,
          },
          shareMediaCategory: "NONE",
        },
      },
      visibility: {
        "com.linkedin.ugc.MemberNetworkVisibility": "PUBLIC",
      },
    };

    const response = await axios.post(
      `${BACKEND_API_BASE}/${customerId}/linkedin/test-post`,
      {
        post_data: postData,
        access_token: accessToken,
        profile_type: "personal",
      },
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`,
        },
      }
    );

    return response.data;
  } catch (error: any) {
    debug.error("LinkedIn personal post error:", error.response?.data || error.message);
    throw error;
  }
}

/**
 * Test posting to LinkedIn Organization Page
 * Uses backend endpoint to avoid CORS issues
 * @param accessToken - LinkedIn access token
 * @param organizationUrn - Organization URN (format: urn:li:organization:123456)
 * @param message - Text to post
 * @param customerId - Customer ID for backend endpoint
 * @param idToken - User's authentication token
 */
export async function testLinkedInOrganizationPost({
  accessToken,
  organizationUrn,
  message,
  customerId,
  idToken,
}: {
  accessToken: string;
  organizationUrn: string;
  message: string;
  customerId: string;
  idToken: string;
}) {
  try {
    // Call backend endpoint to post to LinkedIn (server-to-server, no CORS)
    const BACKEND_API_BASE = process.env.REACT_APP_SOCIAL_API_URL || 'https://bc631mqtg1.execute-api.us-east-1.amazonaws.com/Prod';
    
    const postData = {
      author: organizationUrn,
      lifecycleState: "PUBLISHED",
      specificContent: {
        "com.linkedin.ugc.ShareContent": {
          shareCommentary: {
            text: message,
          },
          shareMediaCategory: "NONE",
        },
      },
      visibility: {
        "com.linkedin.ugc.MemberNetworkVisibility": "PUBLIC",
      },
    };

    const response = await axios.post(
      `${BACKEND_API_BASE}/${customerId}/linkedin/test-post`,
      {
        post_data: postData,
        access_token: accessToken,
        profile_type: "organization",
      },
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`,
        },
      }
    );

    return response.data;
  } catch (error: any) {
    debug.error("LinkedIn organization post error:", error.response?.data || error.message);
    throw error;
  }
}
